/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */
export const config = {
    report: '/report/:slug',
    customReports: '/reports',
    widgetSandbox: '/simple-reports/widget-sandbox'
};

export const defaultWidgetRegistrations = {
    vega: () => import('./components/widgets/VegaReportWidget.svelte'),
    table: () => import('./components/widgets/DataTableWidget.svelte'),
    frame: () => import('./components/widgets/FrameReportWidget.svelte')
};

export const sandboxExampleConfigurations = {
    table: {
        default: {
            title: 'Example table',
            legend:
                '<i style="background: #EDF7ED; color: #367C36; padding: 1px 0.75rem;">{{{icon "check-12"}}}</i> Correct' +
                '<i style="margin-left: 2rem; background: #FFEBEB; color: #DB0000; padding: 1px 0.75rem;">{{{icon "remove-12"}}}</i> Incorrect' +
                '<i style="margin-left: 2rem; background: #FFF5EB; color: #AD5700; padding: 1px 0.75rem;">{{{icon "partial-12"}}}</i> Partial correct' +
                '<i style="margin-left: 2rem; color: #6E6E6E; padding: 1px 0.75rem;">{{{icon "minus-12"}}}</i> Skipped',
            template: {
                rows: 'data',
                columns: [
                    { title: 'Name', sortable: true, metaData: { dataField: '$.{"data": name}' } },
                    { title: 'Score', metaData: { dataField: '$.{ "data": score & "/" & maxscore }' } },
                    {
                        title: '$i+1',
                        fitContent: true,
                        cellComponent: 'ScoreCellComponent',
                        metaData: {
                            type: 'each',
                            columnField: 'data[0].items',
                            dataField: '$.{"status": items[$i].status}'
                        }
                    }
                ]
            },
            data: {
                data: [
                    {
                        name: 'John Doe',
                        score: 12,
                        maxscore: 30,
                        items: [
                            { id: 'item1', status: 'correct' },
                            { id: 'item2', status: 'incorrect' },
                            { id: 'item3', status: 'partially' },
                            { id: 'item4', status: 'skipped' }
                        ]
                    },
                    {
                        name: 'Emmily Brown',
                        score: 11,
                        maxscore: 30,
                        items: [
                            { id: 'item1', status: 'correct' },
                            { id: 'item2', status: 'correct' },
                            { id: 'item3', status: 'partially' }
                        ]
                    }
                ]
            }
        },
        'National center dashboard table': {
            template: {
                rows: 'results',
                columns: [
                    {
                        title: 'Name',
                        cellComponent: 'SchoolNameCell',
                        sortable: true,
                        key: 'schoolId',
                        metaData: {
                            dataField: '$.{ "schoolName": schoolName, "schoolId": schoolId }'
                        }
                    },
                    {
                        title: 'Description',
                        sortable: true,
                        key: 'description',
                        metaData: {
                            dataField: '$.{"data": description}'
                        }
                    },
                    {
                        title: 'Test takers',
                        key: 'testTakers',
                        metaData: {
                            dataField: '$.{"data": testTakers}'
                        },
                        fitContent: true
                    },
                    {
                        title: 'Progression',
                        cellComponent: 'ProgressionCell',
                        metaData: {
                            dataField:
                                '$.{"notStarted": progression.notStarted, "inProgress": progression.inProgress, "finished": progression.finished }'
                        }
                    },
                    {
                        title: '',
                        cellComponent: 'ViewGroupButtonCell',
                        metaData: {
                            dataField: '$.{"data": testTakers}'
                        },
                        fitContent: true
                    }
                ],
                tableOptions: {
                    fullwidth: true
                },
                options: {
                    search: true
                }
            },
            data: {
                results: [...Array(10)].map((item, index) => ({
                    schoolName: `School ${index + 1}`,
                    schoolId: `School ID${index + 1}`,
                    description: index % 2 ? 'This is a short description of the school.' : '',
                    testTakers: 20,
                    progression: {
                        notStarted: 1,
                        inProgress: 10,
                        finished: 3
                    }
                }))
            }
        },
        'School dashboard table': {
            template: {
                rows: 'results',
                columns: [
                    {
                        title: 'Test taker ID',
                        sortable: true,
                        key: 'testTakerId',
                        metaData: {
                            dataField: '$.{"data": testTakerId}'
                        }
                    },
                    {
                        title: 'Battery',
                        sortable: true,
                        key: 'batteryLabel',
                        metaData: {
                            dataField: '$.{"data": batteryLabel}'
                        }
                    },
                    {
                        title: 'Status',
                        sortable: true,
                        key: 'status',
                        metaData: {
                            dataField: '$.{"data": status}'
                        }
                    }
                ],
                tableOptions: {
                    fullwidth: true
                },
                options: {
                    search: true
                }
            },
            data: {
                results: [...Array(10)].map((item, index) => ({
                    testTakerId: `testtakerid${index}`,
                    batteryLabel: 'Battery label',
                    status: 'In progress'
                }))
            }
        }
    },
    frame: {
        default: {
            template: {
                html: `
                <style>
                    .blockCol{
                        display: flex;
                        flex-direction: column;
                        margin: 2rem;
                        font-size: var(--fontsize-body-xs);
                    }
                    .blockCol strong{
                        font-size: var(--fontsize-body);
                    }
                </style>
                <p>This is an HTML example</p>
                <div style="background-color:var(--color-darkSilver);display: flex;">
                    <div class="blockCol">
                        <span>Session started at</span>
                        <span><strong>{{sessionStarted}}</strong></span>
                    </div>
                    <div class="blockCol">
                        <span>Session submited at</span>
                        <span><strong>{{sessionSubmited}}</strong></span>
                    </div>
                    <div class="blockCol">
                        <span>Group</span>
                        <span><strong>{{group}}</strong></span>
                    </div>
                    <div class="blockCol">
                        <span>Time spent</span>
                        <span><strong>{{time}}</strong></span>
                    </div>
                    </div>
                    <table class="svelte-1vzez0v">
                        <thead><tr>
                            <th scope="col" class="svelte-3fysd0">
                                <div class="cell-wrapper svelte-50241w"> <label class="svelte-1r1sj65">
                                    <input type="checkbox" class="checkbox svelte-1r1sj65" aria-checked="false" aria-label="Select All">
                                    <span class="selection-container svelte-1r1sj65"></span>
                                    <span class="selection svelte-1r1sj65"></span>
                                </label></div>
                            </th>
                            <th scope="col" aria-sort="none" tabindex="0" class="svelte-3fysd0">
                                <div class="cell-wrapper svelte-50241w">
                                    <div><div>User</div></div>
                                </div>
                            </th>
                            <th scope="col" class="svelte-3fysd0">
                                <div class="cell-wrapper svelte-50241w">
                                    <div><div>Email</div></div>
                                </div>
                            </th>
                        </tr></thead>
                        <tbody>
                            <tr tabindex="0" role="row" class="svelte-1vzez0v focusable">
                                <td><label class="svelte-1r1sj65">
                                    <input type="checkbox" class="checkbox svelte-1r1sj65" aria-checked="false" aria-label="">
                                    <span class="selection-container svelte-1r1sj65"></span>
                                    <span class="selection svelte-1r1sj65"></span>
                                </label></td>
                                <td>celia</td>
                                <td>celia@example.com</td>
                            </tr>
                            <tr tabindex="0" role="row" class="svelte-1vzez0v focusable">
                                <td><label class="svelte-1r1sj65">
                                    <input type="checkbox" class="checkbox svelte-1r1sj65" aria-checked="false" aria-label="">
                                    <span class="selection-container svelte-1r1sj65"></span>
                                    <span class="selection svelte-1r1sj65"></span>
                                    </label>
                                </td>
                                <td>alice</td>
                                <td>alice@example.com</td>
                            </tr>
                        </tbody>
                    </table>{{{svelte component="Progression" notStarted=2 inProgress=5}}}`
            },
            data: {
                sessionStarted: '06/02/2023 at 10:30 am',
                sessionSubmited: '06/02/2023 at 02:32 pm',
                group: 'Course 1',
                time: '1h 56min'
            }
        },
        'Report summary': {
            template: {
                html: '<style>\r\n  .summary-wrapper {\r\n    display: flex;\r\n    align-items: center;\r\n    height: 100%;\r\n    background-color: var(--color-lightSilver);\r\n    padding: 4rem;\r\n  }\r\n\r\n  .summary {\r\n    display: inline-grid;\r\n    gap: 7rem;\r\n    grid-template-columns: auto;\r\n    grid-auto-flow: column;\r\n  }\r\n\r\n  .summary .summary-group {\r\n    list-style: none;\r\n    margin: 0;\r\n    padding: 0;\r\n    display: grid;\r\n    grid-auto-flow: column;\r\n    gap: 7rem;\r\n    font-size: var(--fontsize-body-s);\r\n  }\r\n\r\n  .summary .summary-divider {\r\n    display: block;\r\n    width: 1px;\r\n    height: 100%;\r\n    background-color: var(--color-border-info);\r\n  }\r\n\r\n  .summary .summary-group li {\r\n    display: flex;\r\n    flex-direction: column;\r\n    color: var(--color-border-info);\r\n  }\r\n\r\n  .summary .summary-group li span:last-child {\r\n    font-weight: bold;\r\n    font-size: var(--fontsize-heading-l);\r\n    color: var(--color-darkerGrey);\r\n  }\r\n</style>\r\n<div class="summary-wrapper">\r\n  <div class="summary">\r\n    <ul class="summary-group">\r\n      <li>\r\n        <span>Start date - End date</span>\r\n        <span\r\n          >{{date session.[0].startDate session.[0].timezone}} - {{date\r\n          session.[0].endDate session.[0].timezone}}</span\r\n        >\r\n      </li>\r\n      <li><span>Group</span> <span>{{session.[0].groupName}}</span></li>\r\n      <li>\r\n        <span>Assigned to</span>\r\n        <span>{{testTakers.total}} test takers</span>\r\n      </li>\r\n    </ul>\r\n    <div class="summary-divider"></div>\r\n    <ul class="summary-group">\r\n      <li>\r\n        <span>Average score</span>\r\n        <span>{{{JSONata "$round(stats.score_stats.avg, 1)"}}}/{{stats.maxScore_stats.avg}}</span>\r\n      </li>\r\n      <li>\r\n        <span>Average time spent</span>\r\n        <span>{{duration stats.duration_stats.avg}}</span>\r\n      </li>\r\n    </ul>\r\n  </div>\r\n</div>\r\n'
            },
            data: {
                stats: {
                    duration_stats: {
                        count: 7,
                        min: 18,
                        max: 94,
                        avg: 78.85714285714286,
                        sum: 552
                    },
                    score_stats: {
                        count: 7,
                        min: 0,
                        max: 5,
                        avg: 1.4285714285714286,
                        sum: 10
                    },
                    maxScore_stats: {
                        count: 7,
                        min: 15,
                        max: 15,
                        avg: 15,
                        sum: 105
                    }
                },
                testTakers: {
                    total: 10
                },
                session: [
                    {
                        groupName: 'usersGroup',
                        endDate: '1690790700000',
                        timezone: 'Europe/Minsk',
                        _id: '0388c0a7-15f7-4182-b2f1-eea6f91c5447'
                    }
                ]
            }
        },
        'Report title': {
            template: {
                html: `<h1>{{title}}</h1>`
            },
            data: {
                title: 'Example report title'
            }
        },
        'National center title': {
            template: {
                html: `
                    <style>
                        .dashboard-title-root {
                            display: flex;
                            align-items: center;
                            height: 100%;
                            width: 100%;
                        }

                        .title {
                            flex-shrink: 0;
                            margin: 0;
                            color: var(--color-gs-dark, #121212);

                            /* Headings/XL (h2) */
                            font-family: Nunito Sans;
                            font-size: 30px;
                            font-style: normal;
                            font-weight: 700;
                            line-height: 40px; /* 133.333% */
                        }

                        .title-dash {
                            color: var(--color-gs-dark, #121212);

                            /* Headings/L (h3) */
                            font-family: Nunito Sans;
                            font-size: 20px;
                            font-style: normal;
                            font-weight: 700;
                            line-height: 24px;
                        }

                        .subtitle {
                            color: var(--color-gs-dark, #121212);

                            /* Paragraph/Body text regular */
                            font-family: Nunito Sans;
                            font-size: 16px;
                            font-style: normal;
                            font-weight: 400;
                            line-height: 24px;
                        }

                        .connector {
                            flex-grow: 1;
                            padding-inline-start: var(--space-2x);
                            padding-inline-end: var(--space-2x);
                        }

                        .connector::before {
                            content: '';
                            display: block;
                            height: 1px;
                            width: 100%;
                            background: var(--color-gs-light-alternative-bg, #F2F2F2);
                        }

                        .meta {
                            display: flex;
                            align-items: center;
                            padding-inline-start: var(--space-2x);
                            padding-inline-end: var(--space-2x);
                        }

                        .meta-category {
                            display: flex;
                            align-items: center;
                            margin-inline-start: var(--space-4x);
                        }

                        .category-title {
                            color: var(--grayscale-b-gs-disabled, #6E6E6E);

                            /* Paragraph/Micro text regular */
                            font-family: Nunito Sans;
                            font-size: 12px;
                            font-style: normal;
                            font-weight: 400;
                            line-height: 16px; /* 133.333% */

                            margin-inline-end: var(--space-1x5);
                        }

                        .category-value {
                            color: var(--grayscale-gs-dark-secondary, #4F4F4F);
                            font-family: Nunito Sans;
                            font-size: 40px;
                            font-style: normal;
                            font-weight: 700;
                            line-height: 40px; /* 100% */
                        }
                    </style>

                    <div class="dashboard-title-root">
                        <h1 class="title">
                            <span>National Center 1</span>
                            <span class="title-dash">&nbsp;-&nbsp;</span>
                            <span class="subtitle">NC1</span>
                        </h1>
                        <div class="connector"></div>
                        <div class="meta">
                            <div class="meta-category">
                                <span class="category-title">Test takers assigned</span>
                                <span class="category-value">240</span>
                            </div>
                            <div class="meta-category">
                                <span class="category-title">Total progression</span>
                                <span>
                                    {{{svelte component="Progression" notStarted=notStarted inProgress=inProgress finished=finished}}}
                                </span>
                            </div>
                        </div>
                    </div>
                `
            },
            data: {
                notStarted: 1,
                inProgress: 10,
                finished: 1
            }
        }
    },
    vega: {
        default: {
            template: {
                spec: {
                    $schema: 'https://vega.github.io/schema/vega/v5.json',
                    background: 'white',
                    padding: 5,
                    style: 'cell',
                    data: [
                        {
                            name: 'source_0',
                            source: 'originaldata',
                            transform: [
                                {
                                    type: 'formula',
                                    expr: "data('originaldata')[0].results",
                                    as: 'results'
                                },
                                {
                                    type: 'flatten',
                                    fields: ['results']
                                },
                                {
                                    type: 'formula',
                                    expr: 'datum.results.score',
                                    as: 'score'
                                },
                                {
                                    type: 'formula',
                                    expr: 'datum.results.count',
                                    as: 'count'
                                }
                            ]
                        },
                        {
                            name: 'data_0',
                            source: 'source_0',
                            transform: [
                                {
                                    type: 'stack',
                                    groupby: ['score'],
                                    field: 'count',
                                    sort: {
                                        field: [],
                                        order: []
                                    },
                                    as: ['items_start', 'items_end'],
                                    offset: 'zero'
                                },
                                {
                                    type: 'filter',
                                    expr: 'isValid(datum["count"]) && isFinite(+datum["count"])'
                                }
                            ]
                        }
                    ],
                    marks: [
                        {
                            name: 'marks',
                            type: 'rect',
                            style: ['bar'],
                            from: {
                                data: 'data_0'
                            },
                            encode: {
                                update: {
                                    fill: {
                                        value: '#006DCC'
                                    },
                                    ariaRoleDescription: {
                                        value: 'bar'
                                    },
                                    description: {
                                        signal: '"label: " + (isValid(datum["score"]) ? datum["score"] : ""+datum["score"]) + "; count: " + (format(datum["count"], ""))'
                                    },
                                    x: {
                                        scale: 'x',
                                        field: 'score'
                                    },
                                    width: {
                                        signal: "max(0.25, bandwidth('x'))"
                                    },
                                    y: {
                                        scale: 'y',
                                        field: 'items_end'
                                    },
                                    y2: {
                                        scale: 'y',
                                        field: 'items_start'
                                    }
                                }
                            }
                        }
                    ],
                    scales: [
                        {
                            name: 'x',
                            type: 'band',
                            domain: {
                                data: 'data_0',
                                field: 'score',
                                sort: true
                            },
                            range: [
                                0,
                                {
                                    signal: 'width'
                                }
                            ],
                            paddingInner: 0.1,
                            paddingOuter: 0.05
                        },
                        {
                            name: 'y',
                            type: 'linear',
                            domain: {
                                data: 'data_0',
                                fields: ['items_start', 'items_end']
                            },
                            range: [
                                {
                                    signal: 'height'
                                },
                                0
                            ],
                            domainMax: 30,
                            zero: true
                        }
                    ],
                    axes: [
                        {
                            scale: 'y',
                            orient: 'left',
                            gridColor: '#BFBFBF',
                            gridScale: 'x',
                            grid: true,
                            tickCount: {
                                signal: 'ceil(height/40)'
                            },
                            domain: false,
                            labels: false,
                            aria: false,
                            maxExtent: 0,
                            minExtent: 0,
                            ticks: false,
                            zindex: 0
                        },
                        {
                            scale: 'x',
                            orient: 'bottom',
                            grid: false,
                            title: 'Score',
                            labelAngle: 0,
                            labelColor: '#4F4F4F',
                            labelFont: 'Nunito Sans',
                            labelFontSize: 14,
                            titleAngle: 0,
                            titleColor: '#4F4F4F',
                            titleFont: 'Nunito Sans',
                            titleFontSize: 14,
                            titleFontWeight: 'normal',
                            titleX: {
                                signal: 'width+20'
                            },
                            titleY: {
                                signal: '10'
                            },
                            labelBaseline: 'top',
                            zindex: 0
                        },
                        {
                            scale: 'y',
                            orient: 'left',
                            grid: false,
                            title: 'Test Takers',
                            domainColor: '#BFBFBF',
                            labelColor: '#4F4F4F',
                            labelFont: 'Nunito Sans',
                            labelFontSize: 14,
                            tickColor: '#BFBFBF',
                            titleAlign: 'left',
                            titleColor: '#4F4F4F',
                            titleFont: 'Nunito Sans',
                            titleFontSize: 14,
                            titleFontWeight: 'normal',
                            titlePadding: -14,
                            titleX: {
                                signal: '-25'
                            },
                            titleY: -20,
                            labelOverlap: true,
                            tickCount: {
                                signal: 'ceil(height/40)'
                            },
                            titleAngle: 0,
                            zindex: 0
                        }
                    ],
                    signals: [
                        {
                            name: 'width',
                            update: '(containerSize()[0])',
                            on: [
                                {
                                    events: {
                                        source: 'window',
                                        type: 'resize'
                                    },
                                    update: 'containerSize()[0]'
                                }
                            ]
                        },
                        {
                            name: 'height',
                            update: '(containerSize()[1])',
                            on: [
                                {
                                    events: {
                                        source: 'window',
                                        type: 'resize'
                                    },
                                    update: 'containerSize()[1]'
                                }
                            ]
                        }
                    ]
                }
            },
            legend:
                '<span style="margin-right: 1rem">Lowest: <b>{{{stats.score_extended_stats.min}}} / {{{stats.score_extended_stats.count}}}</b></span>' +
                '<span style="margin-right: 1rem">Highest: <b>{{{stats.score_extended_stats.max}}} / {{{stats.score_extended_stats.count}}}</b></span>' +
                '<span style="margin-right: 1rem">Median: <b>{{{stats.score_extended_stats.avg}}} / {{{stats.score_extended_stats.count}}}</b></span>' +
                '<span style="margin-right: 1rem">Standard deviation: <b>{{{JSONata "$round(stats.score_extended_stats.std_deviation, 1)"}}}</b></span>',
            data: {
                stats: {
                    score_extended_stats: {
                        count: 27,
                        min: 1,
                        max: 4,
                        avg: 2,
                        sum: 54,
                        sum_of_squares: 156,
                        variance: 1.7777777777777777,
                        variance_population: 1.7777777777777777,
                        variance_sampling: 1.8461538461538463,
                        std_deviation: 1.3333333333333333,
                        std_deviation_population: 1.3333333333333333,
                        std_deviation_sampling: 1.3587324409735149,
                        std_deviation_bounds: {
                            upper: 4.666666666666666,
                            lower: -0.6666666666666665,
                            upper_population: 4.666666666666666,
                            lower_population: -0.6666666666666665,
                            upper_sampling: 4.71746488194703,
                            lower_sampling: -0.7174648819470297
                        }
                    },
                    max_score_extended_stats: {
                        count: 0,
                        min: null,
                        max: null,
                        avg: null,
                        sum: 0,
                        sum_of_squares: null,
                        variance: null,
                        variance_population: null,
                        variance_sampling: null,
                        std_deviation: null,
                        std_deviation_population: null,
                        std_deviation_sampling: null,
                        std_deviation_bounds: {
                            upper: null,
                            lower: null,
                            upper_population: null,
                            lower_population: null,
                            upper_sampling: null,
                            lower_sampling: null
                        }
                    }
                },
                results: [
                    {
                        score: 1,
                        count: 17
                    },
                    {
                        score: 3,
                        count: 3
                    },
                    {
                        score: 4,
                        count: 7
                    }
                ]
            }
        }
    }
};
